/**
 * The voice-note recorder (ADR-002 §24.5), built from the Meetings recorder parts (design meetings-v2 frames 02–09):
 * the `.mv-rec` card with the record / stop button, the red dot and elapsed time, the `.lvl` mic-level bars
 * (useMicLevel), the 2:00 countdown ring, and the vendor notice beside Record every time (`.vnotice`, I6), and beside
 * Try again when the microphone is blocked. Under it,
 * the editor the words stream into ("Hearing: …" for the phrase still being heard), which stays editable throughout.
 *
 * Shared by the Capture screen and the capture link page. Every stop says why (I7): the 2-minute limit, a lost
 * connection, nothing recognised, the service stopping, or a blocked microphone, and the text so far is kept.
 * Firefox (no recogniser) shows the "type it instead" note and no Record.
 */
import type { ReactNode } from 'react';
import { Icon, type IconName } from '../../ui/Icon';
import { VendorNotice } from '../../lib/speech/VendorNotice';
import { CAP_S, type VoiceRecorder } from './useVoiceRecorder';

/** "1:18". */
export const mss = (s: number) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

function Ring({ left }: { left: number }) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const warn = left <= 30 && left < CAP_S;
  return (
    <div className={`mv-ring${warn ? ' warn' : ''}`} role="timer" aria-label={`${mss(left)} left`}>
      <svg viewBox="0 0 84 84">
        <circle className="mv-tr" cx="42" cy="42" r={r} />
        <circle className="mv-pr" cx="42" cy="42" r={r} strokeDasharray={c} strokeDashoffset={c * (1 - left / CAP_S)} />
      </svg>
      <div className="mv-rv">
        <b>{mss(left)}</b>
        <small>{left === CAP_S ? 'max' : 'left'}</small>
      </div>
    </div>
  );
}

function Card({ v, state, head, meta, disabled }: { v: VoiceRecorder; state: 'idle' | 'live' | 'off'; head: ReactNode; meta: ReactNode; disabled?: boolean }) {
  return (
    <div className={`mv-rec${state === 'live' ? ' live' : ''}`}>
      {state === 'live' ? (
        <button type="button" className="mv-rbtn stop" aria-label="Stop recording" onClick={v.stop}>
          <i />
        </button>
      ) : (
        <button type="button" className="mv-rbtn" aria-label="Record" disabled={state === 'off' || disabled || v.rec === 'starting'} onClick={v.record}>
          <Icon name={state === 'off' ? 'micoff' : 'mic'} />
        </button>
      )}
      <div className="mv-rb">
        <div className="mv-rt">
          {state === 'live' ? <span className="rdot" /> : null}
          {head}
          {state === 'live' ? <span className="elapsed">{mss(v.totalS)}</span> : null}
        </div>
        <div className="mv-rm">{meta}</div>
        {state === 'off' ? null : (
          <div className={`lvl ${state === 'live' ? 'on' : 'off'}`} aria-hidden="true">
            {v.level.map((x, i) => (
              <i key={i} style={{ height: state === 'live' ? 6 + Math.round(x * 24) : 4 }} />
            ))}
          </div>
        )}
        {state === 'off' ? null : <VendorNotice />}
      </div>
      {state === 'off' ? null : <Ring left={v.leftS} />}
    </div>
  );
}

const box = (cls: string, icon: IconName, body: ReactNode, role: 'status' | 'alert' = 'status') => (
  <div className={`note ${cls}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14 }} role={role}>
    <Icon name={icon} size={18} />
    <div>{body}</div>
  </div>
);

/** Why the recording stopped (I7). The text so far is always kept. */
function StoppedNote({ v }: { v: VoiceRecorder }) {
  switch (v.why) {
    case 'cap':
      return box('warn', 'alert', <><b>That’s the 2-minute limit.</b> Edit the text, or add the rest by typing.</>);
    case 'network':
      return box(
        'warn',
        'wifioff',
        <>
          <b>Voice typing lost its connection. Your text so far is below. Record again or type.</b>
          <br />
          The speech service needs the internet, so the recording stopped at {mss(v.usedS)}. Nothing you said before that was lost.
        </>,
      );
    case 'nothing':
      return box('warn', 'alert', <><b>Nothing was recognised.</b> Check your microphone and try again.</>);
    case 'restarts':
    case 'error':
      return box('warn', 'alert', <><b>Voice typing stopped. Your text so far is below.</b> Record more, or type the rest.</>);
    case 'blocked':
      return null;
    default:
      return box('good', 'check', <><b>Recording stopped.</b> Read it through and fix names, numbers and dates before you send it. Nothing has been saved yet.</>);
  }
}

/**
 * The recorder above the note's editor (the screen owns the text and the editor). `phone` uses the capture link
 * page's "Tap and talk" heading.
 */
export function LiveRecorder({ v, disabled, phone }: { v: VoiceRecorder; disabled?: boolean; phone?: boolean }) {
  if (!v.voice)
    return box(
      'warn',
      'micoff',
      <>
        <b>Voice typing is not available in this browser. Type it instead.</b>
        <br />
        You can also paste the note. Chrome, Edge and Safari can turn your voice into text.
      </>,
    );

  if (v.why === 'blocked' && !v.recording)
    return (
      <>
        {box(
          'bad',
          'micoff',
          <>
            <b>Voice typing is not available in this browser. Type it instead.</b>
            <br />
            The microphone is blocked, or there isn’t one. To use voice, allow the microphone for this site from the browser’s address bar, then press Try
            again.
            <div style={{ marginTop: 10 }}>
              <button type="button" className="btn sm" onClick={v.record}>
                Try again
              </button>
            </div>
            {/* Try again reopens the microphone, so the notice sits beside it (I6). */}
            <VendorNotice />
          </>,
          'alert',
        )}
        <Card v={v} state="off" head={<span style={{ color: 'var(--mute)' }}>Microphone blocked</span>} meta="Type or paste the note below. It is sent the same way." />
      </>
    );

  if (v.recording) {
    const last = v.leftS <= 30;
    return (
      <Card
        v={v}
        state="live"
        head={v.rec === 'starting' ? 'Starting…' : 'Recording'}
        meta={
          last ? (
            <>
              <b style={{ color: '#B45309' }}>Stops at 2:00: {v.leftS} seconds left.</b> Finish the sentence. You can type the rest afterwards.
            </>
          ) : (
            'Speak normally. Press stop when you are done, or it stops at 2:00.'
          )
        }
      />
    );
  }

  if (v.rec === 'stopped') {
    const none = v.leftS < 1;
    return (
      <>
        <StoppedNote v={v} />
        <Card
          v={v}
          state="idle"
          disabled={disabled || none || !v.online}
          head={none ? 'No recording time left' : `Record more · ${mss(v.leftS)} left`}
          meta={
            !v.online
              ? 'Waiting for the connection. You can still edit the text.'
              : none
                ? 'This note has used its 2 minutes of voice. Type the rest.'
                : 'It adds to the end of the same note.'
          }
        />
      </>
    );
  }

  return (
    <Card
      v={v}
      state="idle"
      disabled={disabled || !v.online}
      head={phone ? 'Tap and talk' : 'Record a voice note'}
      meta={v.online ? 'Speak normally. Press stop when you are done, or it stops at 2:00.' : 'Voice typing needs the internet. Type the note, or wait for the connection.'}
    />
  );
}

/** The phrase still being heard, under the editor while recording (design frame 04). */
export function Hearing({ v }: { v: VoiceRecorder }) {
  if (!v.recording) return null;
  return (
    <div className="hearing" aria-live="polite">
      <span className="rdot" />
      Hearing: {v.interim}
      <span className="caret" />
    </div>
  );
}
